<script setup lang="ts">
import { computed } from 'vue';

import { AppSvgIcon, AppTooltip } from '@libs/ui';

import type { CurrentMonthAlternativeProductDayTile } from './models';
import { PriceMaskType, TileSelectionType } from './models';

interface CurrentMonthAlternativeProductDayTileViewProps {
  calendarTile: CurrentMonthAlternativeProductDayTile;
}

const props = defineProps<CurrentMonthAlternativeProductDayTileViewProps>();

const tooltipTriggerColor = computed(() =>
  props.calendarTile.selection === TileSelectionType.Selected
    ? 'color-white'
    : 'color-secondary-40'
);
</script>

<template>
  <button
    class="current-month-alternative-product-day-tile-view"
    :class="{
      cheap: props.calendarTile.price.type === PriceMaskType.Cheap,
      moderate: props.calendarTile.price.type === PriceMaskType.Moderate,
      expensive: props.calendarTile.price.type === PriceMaskType.Expensive,
      selected: props.calendarTile.selection === TileSelectionType.Selected,
    }"
  >
    <div class="day">
      <div class="day-text">
        {{ props.calendarTile.text }}
      </div>
      <div class="price-mask" />
    </div>
    <div class="price-text">
      {{ props.calendarTile.price.value }}
    </div>

    <AppTooltip position="top-left" class="info-tooltip">
      <template #trigger>
        <AppSvgIcon name="infoCircle" size="16" :color="tooltipTriggerColor" />
      </template>
      <template #default>{{ props.calendarTile.reason }}</template>
    </AppTooltip>
  </button>
</template>

<style scoped lang="scss">
.current-month-alternative-product-day-tile-view {
  width: var(--calendar-tile-size);
  height: var(--calendar-tile-size);
  border-radius: var(--calendar-tile-border-radius);
  background-color: var(--card-tile-background-color);
  transition: background-color var(--transition-short);
  cursor: pointer;

  display: flex;
  gap: var(--spacing-4);
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  @include on-hover {
    background-color: var(--calendar-tile-hover-color);
  }

  &.cheap {
    .day .price-mask {
      background-color: var(--calendar-tile-cheap-color);
    }
  }

  &.moderate {
    .day .price-mask {
      background-color: var(--calendar-tile-moderate-color);
    }
  }

  &.expensive {
    .day .price-mask {
      background-color: var(--calendar-tile-expensive-color);
    }
  }

  &.selected {
    background-color: var(--color-secondary-40);

    @include on-hover {
      background-color: var(--color-secondary-50);
    }

    .day {
      .day-text {
        color: var(--color-white);
      }

      .price-mask {
        background-color: transparent;
      }
    }

    .price-text {
      color: var(--color-white);
    }
  }

  .day {
    position: relative;

    .day-text {
      position: relative;
      z-index: 2;
      color: var(--calendar-tile-day-font-color);
      font: var(--calendar-tile-day-font);
    }

    .price-mask {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-radius: 50%;
      z-index: 1;
      animation: grow 1s forwards;
      animation-delay: 0.5s;

      @keyframes grow {
        0% {
          width: 0;
          height: 0;
        }
        100% {
          width: 24px;
          height: 24px;
        }
      }
    }
  }

  .price-text {
    font: var(--calendar-tile-price-font);
    color: var(--calendar-tile-price-font-color);
  }

  .info-tooltip {
    position: absolute;
    right: var(--calendar-tile-info-icon-right);
    top: var(--calendar-tile-info-icon-top);
  }
}
</style>
